import { useState, useEffect } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { Helmet } from 'react-helmet';

import { countries } from 'data';
import {
  DateInputWithConfirm,
  InputWithConfirm,
  AutocompleteInputWithConfirm,
  getStringFromDate,
  getDateFromString,
  RadioOptionsWithConfirm,
  IQuestion
} from 'shared-components';

import { ApplicationState } from '../../../../../store';
import * as StudentActions from '../../../../../store/student/actions';
import * as VPFActions from '../../../../../store/student/visaPrepForm/actions';
import QuestionPage from '../../../../templates/question';
import { VQ } from '../../../../../store/student/types';
import {
  createNavigateBackwardAction,
  createNavigateForwardAction,
  createNavigateToAction
} from '../../../../../middleware/actions';
import { isValidDate } from '../../../../../utilities/validation';
import { isPersonTravellingWith } from '../../../../../utilities/pages';
import { Pages } from '../../../../../store/student/visaPrepForm/types';
import { getPageStatus } from './utils';
import { toggleTaskMenu } from '../../../../../store/ui/actions';
import { IDate } from '../../../../../types';
import { titleAppend } from '../../../../../utilities/head';

const VisaPrepFormFamilyPage3 = () => {
  // dispatch actions
  const dispatch = useDispatch();
  const onPrevious = () => dispatch(createNavigateBackwardAction());
  const onNext = () => {
    dispatch(StudentActions.saveVisaPrepForm(Pages.PAGE3_3));
    dispatch(createNavigateForwardAction());
  };
  const onSkip = () => dispatch(createNavigateForwardAction());
  const onChange = (fieldName: string, value: any, refValue?: any) =>
    dispatch(VPFActions.createUpdateVPIFieldAction(fieldName, value, refValue));
  const onBackToTaskList = () => {
    dispatch(createNavigateToAction('/dashboard'));
    dispatch(toggleTaskMenu(false));
  };

  // state props from store
  const studentVisaPrepForm = useSelector(({ student }: ApplicationState) => student.visa, shallowEqual);

  const currentStep = 3;
  const steps = 5;
  const nationalitiesNames = countries.map(nationality => nationality.nationality);
  const sectionTitle = 'Family';

  const {
    hasMothersPersonalDetails,
    motherNationality,
    motherSameNationality,
    motherFamilyName,
    motherGivenName,
    motherDob,
    motherPlaceOfBirth,
    motherTravellingWith,
    motherUkVisa,
    motherHasPassport,
    motherPassportNumber
  } = studentVisaPrepForm;

  // component state
  const [complete, setComplete] = useState(false);
  const [dob, setDob] = useState<IDate>({ DD: null, MM: null, YYYY: null });
  const [dobError, setDobError] = useState('');
  const [nationalitySuggestions, setNationalitySuggestions] = useState(nationalitiesNames);

  useEffect(() => {
    if (motherDob && dob.DD === null && dob.MM === null && dob.YYYY === null) {
      const spouseDobFormatted = getDateFromString(motherDob);
      setDob(spouseDobFormatted);
    }
    const { complete: newComplete } = getPageStatus({
      hasMothersPersonalDetails,
      motherNationality,
      motherSameNationality,
      motherFamilyName,
      motherGivenName,
      motherDob,
      motherPlaceOfBirth,
      motherTravellingWith,
      motherUkVisa,
      motherHasPassport,
      motherPassportNumber,
      dobError
    });
    if (newComplete !== complete) {
      setComplete(newComplete);
    }
  }, [
    dob.DD,
    dob.MM,
    dob.YYYY,
    complete,
    hasMothersPersonalDetails,
    motherNationality,
    motherSameNationality,
    motherFamilyName,
    motherGivenName,
    motherDob,
    motherPlaceOfBirth,
    motherTravellingWith,
    motherUkVisa,
    motherHasPassport,
    motherPassportNumber,
    dobError
  ]);

  const onYesNoQuestionButtonChange = (key: VQ, question: IQuestion) => {
    onChange(key, question.label === 'Yes');
  };

  const onNationalityChange = (evt: any) => {
    const value = evt.currentTarget.value || '';
    const suggestions = nationalitiesNames.filter(s => s.toLowerCase().includes(value.toLowerCase()));
    setNationalitySuggestions(suggestions);
    onChange(VQ.MOTHER_NATIONALITY, value);
  };

  const onBlurNationality = (evt: any) => {
    const value = evt.currentTarget.value;
    if (nationalitiesNames.includes(value)) {
      onChange(VQ.MOTHER_NATIONALITY, value);
    } else {
      onChange(VQ.MOTHER_NATIONALITY, '');
    }
    setNationalitySuggestions(nationalitiesNames);
  };

  const validateDate = (dateValue: IDate) => {
    const { YYYY, DD, MM } = dateValue;
    if (YYYY && DD && MM) {
      const isValid = isValidDate(dateValue);
      if (!isValid) {
        setDobError(`Doesn't look like a valid date`);
      } else {
        onChange(VQ.MOTHER_DOB, getStringFromDate(dateValue));
        setDobError('');
      }
    }
  };

  return (
    <QuestionPage
      complete={complete}
      currentStep={currentStep}
      steps={steps}
      onPrevious={onPrevious}
      onNext={onNext}
      onSkip={onSkip}
      onBackToTaskList={onBackToTaskList}
      section={sectionTitle}
    >
      <Helmet>
        <title>
          {`${sectionTitle}: Step ${currentStep}`} {titleAppend}
        </title>
      </Helmet>

      <RadioOptionsWithConfirm
        key={VQ.MOTHERS_PERSONAL_DETAILS}
        id={VQ.MOTHERS_PERSONAL_DETAILS}
        label={`Do you have your Mother's personal details?`}
        onClick={(question: IQuestion) => onYesNoQuestionButtonChange(VQ.MOTHERS_PERSONAL_DETAILS, question)}
        isInline={true}
        questions={[
          {
            char: 'Y',
            label: 'Yes',
            selected: hasMothersPersonalDetails === true
          },
          {
            char: 'N',
            label: 'No',
            selected: hasMothersPersonalDetails === false
          }
        ]}
      />

      {hasMothersPersonalDetails === true ? (
        <>
          <AutocompleteInputWithConfirm
            key={VQ.NATIONALITY}
            id={VQ.NATIONALITY}
            value={motherNationality}
            label={`Mother's Nationality`}
            onChange={onNationalityChange}
            suggestions={nationalitySuggestions}
            onBlur={onBlurNationality}
          />

          <RadioOptionsWithConfirm
            key={VQ.SAME_NATIONALITY}
            id={VQ.SAME_NATIONALITY}
            label={'Have they always had the same nationality?'}
            onClick={(question: IQuestion) => onChange(VQ.MOTHER_SAME_NATIONALITY, question.label === 'Yes')}
            isInline={true}
            questions={[
              {
                char: 'Y',
                label: 'Yes',
                selected: motherSameNationality === true
              },
              {
                char: 'N',
                label: 'No',
                selected: motherSameNationality === false
              }
            ]}
          />

          <InputWithConfirm
            key={VQ.FAMILY_NAME}
            id={VQ.FAMILY_NAME}
            value={motherFamilyName}
            label={`Mother's Family Name`}
            onChange={(evt: any) => onChange(VQ.MOTHER_FAMILY_NAME, evt.currentTarget.value)}
          />

          <InputWithConfirm
            key={VQ.GIVEN_NAME}
            id={VQ.GIVEN_NAME}
            value={motherGivenName}
            label={`Mother's Given Name`}
            onChange={(evt: any) => onChange(VQ.MOTHER_GIVEN_NAME, evt.currentTarget.value)}
          />

          <DateInputWithConfirm
            key={VQ.DOB}
            id={VQ.DOB}
            label={`Mother's Date of Birth`}
            value={dob}
            errorText={dobError}
            onChangeDD={(evt: any) => {
              const spouseDob = {
                ...dob,
                DD: evt.currentTarget.value
              };
              setDob(spouseDob);
            }}
            onChangeMM={(evt: any) => {
              const spouseDob = {
                ...dob,
                MM: evt.currentTarget.value
              };
              setDob(spouseDob);
            }}
            onChangeYY={(evt: any) => {
              const value: string = evt.currentTarget.value;
              if (value.length === 4) {
                const spouseDob = {
                  ...dob,
                  YYYY: value
                };
                setDob(spouseDob);

                validateDate({
                  ...dob,
                  YYYY: value
                });
              } else {
                const spouseDob = {
                  ...dob,
                  YYYY: value
                };
                setDob(spouseDob);
              }
            }}
            onBlur={(evt: any) =>
              validateDate({
                ...dob
              })
            }
          />

          <InputWithConfirm
            key={VQ.PLACE_OF_BIRTH}
            id={VQ.PLACE_OF_BIRTH}
            value={motherPlaceOfBirth}
            label={`Mother's Place of birth`}
            onChange={(evt: any) => onChange(VQ.MOTHER_PLACE_OF_BIRTH, evt.currentTarget.value)}
          />

          <RadioOptionsWithConfirm
            key={VQ.TRAVELLING_WITH}
            id={VQ.TRAVELLING_WITH}
            label={'Is this person travelling with you?'}
            onClick={(question: IQuestion) => onChange(VQ.MOTHER_TRAVELLING_WITH, question.label)}
            questions={[
              {
                char: '1',
                label: 'Yes - under 1 month',
                selected: motherTravellingWith === 'Yes - under 1 month'
              },
              {
                char: '2',
                label: 'Yes - over 1 month',
                selected: motherTravellingWith === 'Yes - over 1 month'
              },
              {
                char: '3',
                label: 'No - not travelling',
                selected: motherTravellingWith === 'No - not travelling'
              }
            ]}
          />

          {isPersonTravellingWith(motherTravellingWith) ? (
            <>
              <RadioOptionsWithConfirm
                key={VQ.HAS_UK_VISA}
                id={VQ.HAS_UK_VISA}
                label={'Do they have a visa for the UK?'}
                onClick={(question: IQuestion) => onChange(VQ.MOTHER_UK_VISA, question.label === 'Yes')}
                isInline={true}
                questions={[
                  {
                    char: 'Y',
                    label: 'Yes',
                    selected: motherUkVisa === true
                  },
                  {
                    char: 'N',
                    label: 'No',
                    selected: motherUkVisa === false
                  }
                ]}
              />

              <RadioOptionsWithConfirm
                key={VQ.HAS_PASSPORT}
                id={VQ.HAS_PASSPORT}
                label={'Do they have a Passport or Travel Document?'}
                onClick={(question: IQuestion) => onChange(VQ.MOTHER_HAS_PASSPORT, question.label === 'Yes')}
                isInline={true}
                questions={[
                  {
                    char: 'Y',
                    label: 'Yes',
                    selected: motherHasPassport === true
                  },
                  {
                    char: 'N',
                    label: 'No',
                    selected: motherHasPassport === false
                  }
                ]}
              />

              {motherHasPassport ? (
                <InputWithConfirm
                  key={VQ.PASSPORT_NUMBER}
                  id={VQ.PASSPORT_NUMBER}
                  value={motherPassportNumber}
                  label={'Current Passport or Travel Document Number:'}
                  onChange={(evt: any) => onChange(VQ.MOTHER_PASSPORT_NUMBER, evt.currentTarget.value)}
                />
              ) : null}
            </>
          ) : null}
        </>
      ) : null}
    </QuestionPage>
  );
};
export default VisaPrepFormFamilyPage3;
